/* eslint-disable sort-imports */
import { DealerManagementService } from 'src/services/dealer-management.service';
import { useLocalization } from 'src/utils/useLocalization';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { MODAL_CONTROL_ACTIONS } from 'src/utils/enums/user-store-actions.enum';
import { IUser } from 'src/utils/interfaces/user';
import {
    ADD_NEW_USER_MODAL_LABEL_BUTTON_CANCEL,
    ADD_NEW_USER_MODAL_LABEL_BUTTON_CONFIRM,
} from 'src/localization/resouceKeys';
import UserManagement from './user-management/UserManagement';
import Notification, {
    NotificationType,
} from 'src/components/ui/notification/Notification';
import { useEffect, useRef, useState } from 'react';
import './styles.scss';

type Districts = {
    districtName: string;
    districtCode: number;
    bdmPartyId: number;
    bdmName: string;
    regionCode: number;
    regionName: string;
    areaName: string;
    areaCode: number;
};

type Regions = {
    regionName: string;
    regionCode: string | number;
    regionalManagerPartyId: number;
    regionalManagerName: string;
    regionalWholesaleMngrPartyId: number;
    regionalWholesaleManagerName: string;
    regionalRetailManagerPartyId: number;
    regionalRetailManagerName: string;
};

const FieldUserManagementComponent = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const brand: string = useSelector((state: IUser) => {
        return state.dealerDataInfo?.dealer.brand || 'vw';
    });
    const Tabs = {
        ADD: 'New User',
        UPDATE: 'Update User',
    };
    const [isSuccess, setIsSuccess] = useState<boolean>(false);
    const [isError, setIsError] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [tab, setTab] = useState('ADD');
    const [regions, setRegions] = useState<Regions[]>([]);
    const [districts, setDistricts] = useState<Districts[]>([]);
    const navRef = useRef<HTMLDivElement>(null);
    const [isSuccessDeleted, setIsSuccessDeleted] = useState<boolean>(false);
    const [successNotificationMessage, setSuccessNotificationMessage] =
        useState<string>('');
    const [errorNotificationMessage, setErrorNotificationMessage] =
        useState<string>('');
    const { addNewUser, deleteUser, editUser } = useLocalization();
    const isEditMode = tab === 'UPDATE';

    const scrollTabs = (direction: 'left' | 'right') => {
        if (!navRef.current) return;

        const keys = Object.keys(Tabs);
        const currentIndex = keys.findIndex((k) => k === tab);

        const newIndex =
            direction === 'left'
                ? Math.max(Number('0'), currentIndex - Number('1'))
                : Math.min(
                      keys.length - Number('1'),
                      currentIndex + Number('1'),
                  );

        setTab(keys[newIndex]);

        const newTabElement = navRef.current.querySelectorAll('li')[newIndex];
        if (newTabElement) {
            (newTabElement as HTMLElement).scrollIntoView({
                behavior: 'smooth',
                inline: 'center',
                block: 'nearest',
            });
        }
    };

    async function getDistrictsAndRegions() {
        const service = new DealerManagementService();
        try {
            setIsLoading(true);
            const res = await service.getDistrictsAndRegions();
            if (res?.data) {
                const reg =
                    res.data.regions?.region?.map((e: any) => ({
                        ...e,
                        regionCode: Number(e.regionCode),
                    })) ?? [];
                const dist =
                    res.data.districts?.map((e: any) => ({
                        ...e,
                        districtCode: Number(e.districtCode),
                    })) ?? [];
                setRegions(reg);
                setDistricts(dist);
            }
            setIsLoading(false);
        } catch (error) {
            setIsLoading(false);
            console.error(error);
        }
    }

    useEffect(() => {
        getDistrictsAndRegions();
    }, []);

    const handleActiveModal = () => {
        dispatch({
            type: MODAL_CONTROL_ACTIONS.SET_ACTIVE,
            payload: {
                titleContent: 'Are you sure you want to leave?',
                bodyContent: 'Leaving this form will discard all your changes.',
                cancelBtnLabel: t(ADD_NEW_USER_MODAL_LABEL_BUTTON_CANCEL),
                confirmBtnLabel: t(ADD_NEW_USER_MODAL_LABEL_BUTTON_CONFIRM),
                onCancelHandler: () => {
                    dispatch({
                        type: MODAL_CONTROL_ACTIONS.SET_SHOW,
                        payload: { shouldShow: false },
                    });
                },
                onConfirmHandler: () => {
                    navigate('/dashboard/systemadmin');
                },
            },
        });
    };

    useEffect(() => {
        handleActiveModal();
        return () => {
            dispatch({ type: MODAL_CONTROL_ACTIONS.SET_INACTIVE });
        };
    }, []);

    const successMsg =
        successNotificationMessage ||
        (isEditMode
            ? editUser.NOTIFICATION_SUCCESS_REQUEST
            : addNewUser.NOTIFICATION_SUCCESS);
    const errorMsg =
        errorNotificationMessage ||
        (isEditMode
            ? editUser.NOTIFICATION_ERROR_REQUEST
            : addNewUser.NOTIFICATION_ERROR_REQUEST);

    useEffect(() => {
        setIsSuccess(false);
        setIsError(false);
        setIsSuccessDeleted(false);
        setSuccessNotificationMessage('');
        setErrorNotificationMessage('');
    }, [tab]);

    return (
        <>
            <div
                className={`field-user-container__nav-header ${brand.toLowerCase()}`}
            >
                <div className='one'>
                    <button
                        className='c-btn c-btn--link c-btn--small c-btn--simple'
                        type='button'
                        onClick={() => {
                            dispatch({
                                type: MODAL_CONTROL_ACTIONS.SET_SHOW,
                                payload: { shouldShow: true },
                            });
                        }}
                    >
                        <i
                            className='c-btn__icon c-icon c-icon--[semantic-back]'
                            aria-hidden='true'
                            role='img'
                        ></i>
                        <span className='c-btn__text'>
                            {addNewUser.HEADER_BUTTON_LABEL}
                        </span>
                    </button>
                </div>
            </div>
            <div className='field-user-wrapper'>
                {isSuccess && (
                    <div className={'success-notification'}>
                        <Notification
                            content={successMsg}
                            type={NotificationType.SUCCESS}
                            visibility={isSuccess}
                            setVisibility={setIsSuccess}
                        />{' '}
                    </div>
                )}
                {isError && (
                    <Notification
                        content={errorMsg}
                        type={NotificationType.ERROR}
                        visibility={isError}
                        setVisibility={setIsError}
                    />
                )}
                {isSuccessDeleted && (
                    <div className={'success-notification'}>
                        <Notification
                            content={deleteUser.NOTIFICATION_SUCCESS_DELETION}
                            type={NotificationType.SUCCESS}
                            visibility={isSuccessDeleted}
                            setVisibility={setIsSuccessDeleted}
                        />{' '}
                    </div>
                )}
                <div className='page-title'>
                    <h5>Field User Management</h5>
                </div>
                <div className='content-container'>
                    <div className='c-tabs'>
                        <div className='c-tabs__nav-container'>
                            <nav aria-label='Tabs Navigation'>
                                <button
                                    className='c-tabs__arrow c-tabs__arrow--left'
                                    onClick={() => scrollTabs('left')}
                                >
                                    &lt;
                                </button>
                                <div className='c-tabs__nav' ref={navRef}>
                                    {Object.entries(Tabs).map((ele, index) => {
                                        return (
                                            <div
                                                key={`${index}-${ele}`}
                                                className='c-tabs__nav-item'
                                            >
                                                <button
                                                    className={`c-tabs__nav-link ${
                                                        tab == ele[0]
                                                            ? 'is-active'
                                                            : ''
                                                    }`}
                                                    onClick={() => {
                                                        setTab(ele[0]);
                                                    }}
                                                    type='button'
                                                >
                                                    <a
                                                        className='c-link'
                                                        data-testid='link-item-faqs'
                                                    >
                                                        <span className='c-link__body'>
                                                            {ele[1]}
                                                        </span>
                                                    </a>
                                                </button>
                                            </div>
                                        );
                                    })}
                                </div>
                                <button
                                    className='c-tabs__arrow c-tabs__arrow--right'
                                    onClick={() => scrollTabs('right')}
                                >
                                    &gt;
                                </button>
                            </nav>
                        </div>
                    </div>
                    <div className='tab'>
                        <UserManagement
                            setSuccessNotification={setIsSuccess}
                            setErrorNotification={setIsError}
                            setSuccessNotificationMessage={
                                setSuccessNotificationMessage
                            }
                            setErrorNotificationMessage={
                                setErrorNotificationMessage
                            }
                            setIsSuccessDeleted={setIsSuccessDeleted}
                            regions={regions}
                            districts={districts}
                            isLoadingDistAndReg={isLoading}
                            isEditMode={isEditMode}
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

export default FieldUserManagementComponent;
